import { FunctionComponent, useState, useEffect } from "react";
import Row from "./Row";
import DropdownMenu from "./dropdown/DropdownMenu";
import Text from "./Text";
import styled from "styled-components";
import { IKanban } from "../interfaces/models/kanban";
import { styles } from "../styles/themes/style";
import { Id } from "../pages/canvas/sections/kanban/types";
import { useConnectionsHandlers } from "../services/actions/connections/useConnectionsHandlers";
import { useAppHandlers } from "../services/actions/app/useAppHandlers";
import { useAppStore } from "../stores/application/useAppStore";
import { ModalActionType } from "./modal/AppModal";
import { useTagsHandlers } from "../services/actions/tags/useTagsHandlers";
import Input from "./Input";
import SvgFragment from "../fragments/SvgFragment";

interface IKanbanInfoPopupProps {
  kanban?: IKanban;
  allKanbans?: IKanban[];
  updateKanban?: (id: Id, value: string, isName: boolean) => void;
  removeKanban?: (kanbanToDelete: IKanban) => void;
  onKanbanChange?: (kanbanId: string) => void;
  onKanbansListUpdate?: () => void;
  onUpdateKanbanInList?: (updatedKanban: IKanban) => void;
}

const KanbanInfoPopup: FunctionComponent<IKanbanInfoPopupProps> = ({
  kanban,
  allKanbans = [],
  updateKanban,
  removeKanban,
  onKanbanChange,
  onKanbansListUpdate,
  onUpdateKanbanInList
}) => {
  const { createKanbanHandler, updateKanbanHandler } = useConnectionsHandlers();
  const { setEditKanbanModal } = useAppHandlers();
  const { store: { modal } } = useAppStore();
  const { loadAllTagsHandler } = useTagsHandlers();

  const [search, setSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isEditKanbanModalOpen = modal?.type === ModalActionType.EDIT_KANBAN_MODAL;
  const isDestructiveModalOpen = modal?.type === ModalActionType.DESTRUCTIVE_MODAL;
  
  // Check if destructive modal is for deleting kanban (contains "Pipeline" in headingText)
  // vs deleting column (doesn't contain "Pipeline")
  const isDeleteKanbanModal = isDestructiveModalOpen && 
    modal?.headingText?.toString().includes("Pipeline");
  
  // Keep dropdown open for Edit Kanban modal OR destructive modal for deleting kanban
  const shouldKeepOpen = isEditKanbanModalOpen || isDeleteKanbanModal;

  const handleToggle = (nextIsOpen: boolean) => {
    // Don't allow closing if Edit Kanban modal is open
    if (!nextIsOpen && shouldKeepOpen) {
      return;
    }
    setIsDropdownOpen(nextIsOpen);
    if (nextIsOpen) {
      setSearch("");
    }
  };

  // Keep dropdown open when Edit Kanban modal is open
  useEffect(() => {
    if (shouldKeepOpen) {
      if (!isDropdownOpen) {
        setIsDropdownOpen(true);
      }
    }
  }, [shouldKeepOpen, isDropdownOpen]);

  const handleEditKanban = (e: React.MouseEvent, k: IKanban) => {
      e.stopPropagation();
      const allKanbansCount = allKanbans.length;
      setEditKanbanModal(k, async (data) => {
          const res = await updateKanbanHandler(k._id, data);
          if (res?.success && res?.data) {
             // Update the kanban in the list with the returned kanban from backend
             if (onUpdateKanbanInList) {
                 onUpdateKanbanInList(res.data);
             }
             // Also refresh from server to ensure consistency
             if (onKanbansListUpdate) {
                 await onKanbansListUpdate();
             }
          }
      }, undefined, () => {
          // Pass the kanban being edited to the delete handler
          if (removeKanban) {
              removeKanban(k);
          }
      }, allKanbansCount);
  };

  const handleCreateKanban = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent dropdown close
      setEditKanbanModal(undefined, async (data) => {
          const res = await createKanbanHandler(data);
          if (res?.success) {
              // Refresh tags store to include any new kanban tags
              loadAllTagsHandler();
              
              if (onKanbansListUpdate) {
                  await onKanbansListUpdate();
              }
              if (onKanbanChange && res.data?._id) {
                  onKanbanChange(res.data._id);
              }
          }
      });
  }

  const handleSelectKanban = (k: IKanban) => {
      if (onKanbanChange) {
          onKanbanChange(k._id);
      }
  };

  const filteredKanbans = allKanbans.filter(k => k.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <StyledContainer>
      <DropdownMenu
        $unsetOverflow
        $transparent
        title={kanban ? kanban.name : `Pipeline 1`}
        $color={styles.colors.black600}
        $fontSize="18px"
        autoClose={shouldKeepOpen ? undefined : "outside"}
        forceShow={shouldKeepOpen ? true : undefined}
        show={isDropdownOpen}
        onToggle={handleToggle}
      >
        <DropdownContent onClick={(e) => e.stopPropagation()}>
            <FixedHeader>
                <Input 
                    value={search}
                    onChange={(e: any) => setSearch(e.target.value)}
                    placeholder="Search pipelines..."
                    search
                />
            </FixedHeader>
            
            <ScrollableList>
                {filteredKanbans.length === 0 ? (
                    <LoadingText>No pipelines found</LoadingText>
                ) : (
                    filteredKanbans.map(k => (
                        <ListItem 
                            key={k._id} 
                            onClick={() => handleSelectKanban(k)}
                            isSelected={kanban?._id === k._id}
                        >
                            <ItemContent>
                                <KanbanName>{k.name}</KanbanName>
                                {kanban?._id === k._id && <SvgFragment type="checkmark" />}
                            </ItemContent>
                            <EditButton onClick={(e) => handleEditKanban(e, k)}>
                                <SvgFragment type="pencil" />
                            </EditButton>
                        </ListItem>
                    ))
                )}
            </ScrollableList>
            
            <FixedFooter onClick={handleCreateKanban}>
                <SvgFragment type="plus" />
                <Text $active>Create new pipeline</Text>
            </FixedFooter>
        </DropdownContent>
      </DropdownMenu>
    </StyledContainer>
  );
};

const StyledContainer = styled(Row)`
  min-width: 420px;
  gap: 10px;
  align-items: center;

  & .actions-dropdown + div {
    width: 360px;
    max-height: 329px;
    padding: 0;
    overflow: hidden;
    border-radius: 4px;
  }
`;

const DropdownContent = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 329px;
    background: white;
    overflow: hidden;
`;

const FixedHeader = styled.div`
    padding: 16px;
    padding-bottom: 0;
    flex-shrink: 0;
`;

const ScrollableList = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 17px 16px;
    display: flex;
    flex-direction: column;
    gap: 13px;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${styles.colors.black200};
        border-radius: 3px;
    }
`;

const LoadingText = styled(Text)`
    text-align: center;
    color: ${styles.colors.black400};
    padding: 0 8px;
    height: 32px;
`;

const EditButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 24px; 
    height: 24px;
    margin-left: 8px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    
    svg { 
        path { 
            fill: ${styles.colors.black300}; 
            transition: fill 0.2s;
        } 
    }
    
    &:hover {
        svg path {
            fill: ${styles.colors.primary600};
        }
    }
`;

const ListItem = styled.div<{ isSelected?: boolean }>`
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    position: relative;

    &:hover {
        background-color: ${styles.colors.black100};
        
        ${EditButton} {
            opacity: 1;
            pointer-events: auto;
        }
    }
`;

const ItemContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    
    svg { 
        width: 24px; 
        height: 24px; 
        flex-shrink: 0;
        path {
            fill: ${styles.colors.primary600}; 
            transition: fill 0.2s;
        } 
    }
`;

const KanbanName = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
    display: block !important;
    max-width: 100%;
`;

const FixedFooter = styled.div`
    padding: 16px;
    border-top: 1px solid ${styles.colors.black100};
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    flex-shrink: 0;
    background: white;
    
    &:hover {
        background-color: ${styles.colors.black100};
    }

    svg { width: 12px; height: 12px; path { fill: ${styles.colors.primary600}; } }
`;

export default KanbanInfoPopup;
